import _ from 'lodash'
import store, {storage} from '@/store'
import {AuthFieldsModel} from '@/models/AuthFieldsModel'
import {runtimeConditionsAreMet} from '@/utils/auth/validation'

let askedAfterLoginData = []

function verificationFieldIsEmpty(customer, field) {
  /**
   * Password because this is not meant for reset/update password
   * Terms are now required in the signup process and SMS login
   * Friend invite because I'm not sure if we are still using that feature on web, I haven't seen that type in any form yet.
   * This can be changed/improved of course when we implement a proper verification screen, but for now it works as a hotfix.
   */
  const ignoredFields = [
    'password',
    'passwordRepeat',
    'terms_and_privacy',
    'friend_invite'
  ]

  if (ignoredFields.includes(field.key)) {
    return false
  }

  if (field.identity && field.identity === 'custom_fields') {
    const customFields = _.get(customer, 'custom_fields')

    if (!_.isEmpty(customFields)) {
      const foundCustomField = customFields.find((customField) => {
        return customField.key === field.key
      })

      if (foundCustomField) {
        const value = _.get(foundCustomField, 'value')

        // Lodash returns true if the value is a Boolean or a Number
        if (_.isBoolean(value) || _.isNumber(value)) {
          return false
        }

        return _.isEmpty(value)
      }
    }
  }

  if (field.key === 'firstName') {
    return _.isEmpty(_.get(customer, 'first_name'))
  }

  if (field.key === 'lastName') {
    return _.isEmpty(_.get(customer, 'last_name'))
  }

  if (
    field.key === 'city'
      || field.key === 'street'
      || field.key === 'city_code'
      || field.key === 'street_number'
      || field.key === 'country'
      || field.key === 'state'
  ) {
    return _.isEmpty(_.get(customer.address, field.key))
  }

  if (field.key === 'phone') {
    return _.isEmpty(_.get(customer, 'phone_number'))
  }

  if (_.has(customer, field.key)) {
    return _.isEmpty(_.get(customer, field.key))
  }

  return true
}

function fieldIsAskedAfterLogin(field) {
  const dataArray =
    (field.identity && field.identity === 'custom_fields')
      ? _.get(askedAfterLoginData, 'customUserFields', [])
      : _.get(askedAfterLoginData, 'userAttributes', [])

  if (!_.isEmpty(dataArray)) {
    return dataArray.includes(field.key)
  }

  return false
}

function registrationFieldShouldBeDisplayed(field) {
  if (
    _.get(field, 'required', false) ||
    _.get(field, 'ask_after_login', false)
  ) {
    return true
  }

  return false
}

function verificationFieldShouldBeDisplayed(customer, field) {
  // Skip fields whose conditions the profile doesn't meet, otherwise
  // the layout hides them and the user is sent to an empty form.
  if (!runtimeConditionsAreMet(customer, field.required_conditions)) {
    return false
  }

  if (
    verificationFieldIsEmpty(customer, field)
      && (
        _.get(field, 'required', false)
          || (
            _.get(field, 'ask_after_login', false)
              && !fieldIsAskedAfterLogin(field)
            )
      )
  ) {
    return true
  }

  return false
}

function fieldShouldBeDisplayed(customer, field) {
  if (_.isEmpty(customer)) {
    return registrationFieldShouldBeDisplayed(field)
  } else {
    return verificationFieldShouldBeDisplayed(customer, field)
  }
}

function filterFields(customer, fields) {
  return fields.filter((field) => {
    return fieldShouldBeDisplayed(customer, field)
  })
}

function setSpecialRequiredField(attributes, field) {
  switch (field.key) {
    case 'password':
      field.required = true
      break
    case 'passwordRepeat':
      field.required = true
      field.metadata = {compareTo: 'password'}
      break
    case 'email':
      field.required = true
      break
    case 'firstName':
    case 'lastName':
      // Name field defines if firstName and lastName are required or not
      // Name field also defines if firstName and lastName should ask after login or not
      field.required = _.get(attributes, 'name.required', false)
      field.ask_after_login = _.get(attributes, 'name.ask_after_login', false)
      field.validators = _.get(attributes, 'name.validators', null)
      break
    case 'name':
      field.required = false
      break
    case 'terms_and_privacy':
      field.required = true
      break
  }
}

function setFieldSpecialAttributes(field) {
  if (
    field.type === 'select' &&
    _.get(field, 'metadata.choices[0]')
  ) {
    if (field.required) {
      field.metadata.choices[0].title += ' *'
    }
  }
}

export function createAuthAttributes(config) {
  const signupMode = _.get(config, 'signupMode', true)
  const signupSteps = _.get(
    store.getters, 'authSettings.signup_steps', []
  )
  const customFields = _.cloneDeep(
    _.get(store.getters, 'customUserFields')
  )
  const authAttributes = _.get(store.getters, 'authSettings.attributes')
  const authFields = _.cloneDeep(AuthFieldsModel)
  const customer = _.get(store.getters, 'user', null)
  let populatedGroups = []

  if (!signupMode) {
    askedAfterLoginData = JSON.parse(storage.getItem('askedUserFields'))
  }

  if (_.isEmpty(signupSteps)) {
    const populatedFields = authFields.map((field) => {
      if (authAttributes && _.get(authAttributes, field.key)) {
        return {...field, ...authAttributes[field.key]}
      } else {
        return field
      }
    })

    populatedFields.forEach((field) => {
      setSpecialRequiredField(authAttributes, field)
    })

    populatedFields.sort((a, b) => (a.order > b.order) ? 1 : -1)

    const termsPrivacyField = signupMode
      ? _.remove(populatedFields, (field) => {
          return field.key === 'terms_and_privacy'
        })
      : null

    // We use firstName and lastName instead
    _.remove(populatedFields, (field) => {
      return field.key === 'name'
    })

    const filteredAttributes = [
      ...filterFields(customer, populatedFields),
      ...filterFields(customer, customFields)
    ].map((attribute) => {
      setFieldSpecialAttributes(attribute)
      return attribute
    })

    if (!_.isEmpty(termsPrivacyField)) {
      filteredAttributes.push(termsPrivacyField[0])
    }

    populatedGroups = [[{
      attributes: filteredAttributes,
      title: '',
      description: '',
      key: undefined
    }]]
  } else {
    populatedGroups = signupSteps.map(({groups}) => groups.map((group) => {
      const attributes = group.attributes.map((attribute) => {
        if (attribute.type === 'custom_user_field') {
          const foundCustomField = customFields.find((customField) => {
            return customField.key === attribute.key
          })

          if (foundCustomField) {
            attribute = foundCustomField
          }
        } else {
          const modelField = authFields.find((field) => {
            return field.key === attribute.key
          })

          if (modelField) {
            attribute = {...attribute, ...modelField}
          }

          const foundAuthAttribute = _.get(
            authAttributes, attribute.key, null
          )

          if (foundAuthAttribute) {
            attribute = {...attribute, ...foundAuthAttribute}
          }

          setSpecialRequiredField(authAttributes, attribute)
        }

        setFieldSpecialAttributes(attribute)

        return attribute
      })
      return ({
        attributes: filterFields(customer, attributes),
        title: group.title,
        description: group.description,
        key: group.componentKey
      })
    }))
  }

  return populatedGroups
}
